import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AddScammer = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    twitterId: '',
    googleDriveLink: '',
    reason: ''
  });
  const [profileLinks, setProfileLinks] = useState([{ websiteName: '', link: '' }]);
  const [alts, setAlts] = useState([{ twitterName: '', twitterId: '' }]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [proofScreenshots, setProofScreenshots] = useState([]);
  const [proofPreviews, setProofPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileLinkChange = (index, field, value) => {
    const newLinks = [...profileLinks];
    newLinks[index][field] = value;
    setProfileLinks(newLinks);
  };

  const addProfileLink = () => {
    setProfileLinks([...profileLinks, { websiteName: '', link: '' }]);
  };

  const removeProfileLink = (index) => {
    const newLinks = profileLinks.filter((_, i) => i !== index);
    setProfileLinks(newLinks);
  };

  const handleAltChange = (index, field, value) => {
    const newAlts = [...alts];
    newAlts[index][field] = value;
    setAlts(newAlts);
  };

  const addAlt = () => {
    setAlts([...alts, { twitterName: '', twitterId: '' }]);
  };

  const removeAlt = (index) => {
    const newAlts = alts.filter((_, i) => i !== index);
    setAlts(newAlts);
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePicture(file);
      setProfilePicturePreview(URL.createObjectURL(file));
    }
  };

  const handleProofScreenshotsChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const currentTotal = proofScreenshots.length;
    const remainingSlots = 10 - currentTotal;
    
    if (remainingSlots <= 0) {
      alert('Maximum 10 screenshots allowed');
      return;
    }
    
    const filesToAdd = newFiles.slice(0, remainingSlots);
    const updatedFiles = [...proofScreenshots, ...filesToAdd];
    const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
    const updatedPreviews = [...proofPreviews, ...newPreviews];
    
    setProofScreenshots(updatedFiles);
    setProofPreviews(updatedPreviews);
    
    // Reset the input so the same file can be selected again if needed
    e.target.value = '';
  };

  const removeProofScreenshot = (index) => {
    const newFiles = proofScreenshots.filter((_, i) => i !== index);
    const newPreviews = proofPreviews.filter((_, i) => i !== index);
    setProofScreenshots(newFiles);
    setProofPreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.reason || !profilePicture) {
      alert('Please fill in all required fields and upload a profile picture');
      return;
    }

    // Filter out empty profile links
    const validProfileLinks = profileLinks.filter(link => link.websiteName && link.link);
    
    // Filter out empty alts
    const validAlts = alts.filter(alt => alt.twitterName && alt.twitterId);

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('twitterId', formData.twitterId);
    submitData.append('googleDriveLink', formData.googleDriveLink);
    submitData.append('reason', formData.reason);
    submitData.append('profileLinks', JSON.stringify(validProfileLinks));
    submitData.append('alts', JSON.stringify(validAlts));
    submitData.append('profilePicture', profilePicture);
    
    proofScreenshots.forEach((file) => {
      submitData.append('proofScreenshots', file);
    });

    setSubmitting(true);
    try {
      await axios.post('/api/scammers', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      alert('Scammer added successfully!');
      navigate('/scammers');
    } catch (error) {
      console.error('Error adding scammer:', error);
      alert(error.response?.data?.message || 'Failed to add scammer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '950px', margin: '0 auto', padding: '30px 20px' }}>
      {/* Header */}
      <div style={{ 
        marginBottom: '35px',
        padding: '35px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.15) 0%, rgba(153, 27, 27, 0.1) 100%)',
        border: '1px solid rgba(220, 38, 38, 0.3)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-30px',
          right: '-30px',
          fontSize: '150px',
          opacity: '0.05'
        }}>➕</div>
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            marginBottom: '12px',
            fontWeight: '800',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
          }}>
            ➕ Add Scammer
          </h1>
          <p style={{ 
            color: 'var(--text-secondary)', 
            fontSize: '1.1rem',
            lineHeight: '1.6'
          }}>
            Document a scammer to help protect the community
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card" style={{ 
        padding: '40px',
        background: 'var(--card-bg)',
        borderRadius: '16px',
        border: '1px solid var(--border)'
      }}>
        {/* Name */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)'
          }}>
            Name <span style={{ color: '#dc2626', fontSize: '1.1rem' }}>*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter the scammer's name or alias"
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              border: '2px solid var(--border)',
              color: 'var(--text)',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--accent)';
              e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Profile Picture */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)'
          }}>
            Profile Picture <span style={{ color: '#dc2626', fontSize: '1.1rem' }}>*</span>
          </label>
          <div style={{
            position: 'relative',
            border: '2px dashed var(--border)',
            borderRadius: '12px',
            padding: '20px',
            background: 'rgba(0, 0, 0, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)';
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
          }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              required
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: 'pointer'
              }}
            />
            {!profilePicturePreview ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '10px' }}>📸</div>
                <p style={{ color: 'var(--text)', marginBottom: '5px', fontWeight: '600' }}>
                  Click to upload or drag and drop
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  PNG, JPG, GIF up to 10MB
                </p>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <img
                  src={profilePicturePreview}
                  alt="Profile preview"
                  style={{ 
                    width: '250px', 
                    height: '250px', 
                    objectFit: 'cover', 
                    borderRadius: '12px',
                    border: '3px solid var(--border)',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)'
                  }}
                />
                <p style={{ marginTop: '12px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Click to change image
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Profile Links */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'flex', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)',
            alignItems: 'center',
            gap: '8px'
          }}>
            🔗 Profile Links
          </label>
          <div style={{
            background: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '12px',
            padding: '20px'
          }}>
            {profileLinks.map((link, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                gap: '12px', 
                marginBottom: index < profileLinks.length - 1 ? '12px' : '0',
                alignItems: 'stretch'
              }}>
                <input
                  type="text"
                  placeholder="Website (e.g., Twitter, Discord)"
                  value={link.websiteName}
                  onChange={(e) => handleProfileLinkChange(index, 'websiteName', e.target.value)}
                  style={{
                    flex: '0 0 180px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    background: 'var(--card-bg)',
                    border: '2px solid var(--border)',
                    color: 'var(--text)',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
                <input
                  type="url"
                  placeholder="https://..."
                  value={link.link}
                  onChange={(e) => handleProfileLinkChange(index, 'link', e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    borderRadius: '8px',
                    background: 'var(--card-bg)',
                    border: '2px solid var(--border)',
                    color: 'var(--text)',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                />
                {profileLinks.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeProfileLink(index)}
                    style={{
                      padding: '0 18px',
                      background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addProfileLink}
              style={{
                marginTop: '15px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)',
                border: '2px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '8px',
                color: '#60a5fa',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(37, 99, 235, 0.3) 100%)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(37, 99, 235, 0.2) 100%)';
                e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
              }}
            >
              + Add Another Link
            </button>
          </div>
        </div>

        {/* Twitter ID */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'flex', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)',
            alignItems: 'center',
            gap: '8px'
          }}>
            𝕏 Numerical Twitter ID <span style={{ 
              fontSize: '0.8rem', 
              fontWeight: '500',
              color: 'var(--text-secondary)',
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '3px 8px',
              borderRadius: '6px'
            }}>Optional</span>
          </label>
          <input
            type="text"
            name="twitterId"
            value={formData.twitterId}
            onChange={handleChange}
            placeholder="e.g., 1234567890"
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              border: '2px solid var(--border)',
              color: 'var(--text)',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1d9bf0';
              e.target.style.boxShadow = '0 0 0 3px rgba(29, 155, 240, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{ 
            marginTop: '8px',
            padding: '10px 14px',
            background: 'rgba(29, 155, 240, 0.05)',
            border: '1px solid rgba(29, 155, 240, 0.2)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '1rem' }}>💡</span>
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5' }}>
              Find this in their Twitter profile URL or using tools like tweeterid.com
            </small>
          </div>
        </div>

        {/* Alts (Alternate Twitter Accounts) */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'flex', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)',
            alignItems: 'center',
            gap: '8px'
          }}>
            👥 Known Alts (Alternate Accounts) <span style={{ 
              fontSize: '0.8rem', 
              fontWeight: '500',
              color: 'var(--text-secondary)',
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '3px 8px',
              borderRadius: '6px'
            }}>Optional</span>
          </label>
          <div style={{
            background: 'rgba(29, 155, 240, 0.05)',
            border: '1px solid rgba(29, 155, 240, 0.2)',
            borderRadius: '12px',
            padding: '20px'
          }}>
            {alts.map((alt, index) => (
              <div key={index} style={{ 
                marginBottom: index < alts.length - 1 ? '15px' : '0'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'stretch',
                  marginBottom: '8px'
                }}>
                  <input
                    type="text"
                    placeholder="Twitter Name (e.g., @username)"
                    value={alt.twitterName}
                    onChange={(e) => handleAltChange(index, 'twitterName', e.target.value)}
                    style={{
                      flex: '1',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      background: 'var(--card-bg)',
                      border: '2px solid var(--border)',
                      color: 'var(--text)',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#1d9bf0'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                  />
                  <input
                    type="text"
                    placeholder="Numerical ID"
                    value={alt.twitterId}
                    onChange={(e) => handleAltChange(index, 'twitterId', e.target.value)}
                    style={{
                      flex: '0 0 180px',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      background: 'var(--card-bg)',
                      border: '2px solid var(--border)',
                      color: 'var(--text)',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#1d9bf0'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                  />
                  {alts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAlt(index)}
                      style={{
                        padding: '0 18px',
                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                {alt.twitterId && (
                  <div style={{ paddingLeft: '5px' }}>
                    <a
                      href={`https://twitter.com/i/user/${alt.twitterId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#1d9bf0',
                        fontSize: '0.85rem',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                        e.currentTarget.style.color = '#60a5fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                        e.currentTarget.style.color = '#1d9bf0';
                      }}
                    >
                      🔗 View Twitter Profile
                      <span style={{ fontSize: '0.75rem' }}>↗</span>
                    </a>
                  </div>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addAlt}
              style={{
                marginTop: '15px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, rgba(29, 155, 240, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
                border: '2px solid rgba(29, 155, 240, 0.3)',
                borderRadius: '8px',
                color: '#1d9bf0',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '0.95rem',
                transition: 'all 0.3s ease',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 155, 240, 0.3) 0%, rgba(59, 130, 246, 0.3) 100%)';
                e.currentTarget.style.borderColor = 'rgba(29, 155, 240, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(29, 155, 240, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)';
                e.currentTarget.style.borderColor = 'rgba(29, 155, 240, 0.3)';
              }}
            >
              + Add Another Alt
            </button>
            <div style={{ 
              marginTop: '12px',
              padding: '10px 14px',
              background: 'rgba(29, 155, 240, 0.05)',
              border: '1px solid rgba(29, 155, 240, 0.15)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <span style={{ fontSize: '1rem', marginTop: '2px' }}>💡</span>
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: '1.5' }}>
                Alternate accounts this scammer uses. Click the profile link to verify the numerical ID matches the account.
              </small>
            </div>
          </div>
        </div>

        {/* Google Drive Link */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'flex', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)',
            alignItems: 'center',
            gap: '8px'
          }}>
            📁 Google Drive Link <span style={{ 
              fontSize: '0.8rem', 
              fontWeight: '500',
              color: 'var(--text-secondary)',
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '3px 8px',
              borderRadius: '6px'
            }}>Optional</span>
          </label>
          <input
            type="url"
            name="googleDriveLink"
            value={formData.googleDriveLink}
            onChange={handleChange}
            placeholder="https://drive.google.com/..."
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              border: '2px solid var(--border)',
              color: 'var(--text)',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#22c55e';
              e.target.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{ 
            marginTop: '8px',
            padding: '10px 14px',
            background: 'rgba(34, 197, 94, 0.05)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '1rem' }}>💡</span>
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5' }}>
              Link to a folder with additional evidence
            </small>
          </div>
        </div>

        {/* Reason */}
        <div style={{ marginBottom: '30px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)'
          }}>
            Reason <span style={{ color: '#dc2626', fontSize: '1.1rem' }}>*</span>
          </label>
          <textarea
            name="reason"
            value={formData.reason}
            onChange={handleChange}
            required
            placeholder="Explain why this person is being listed as a scammer. Be specific and factual."
            style={{
              width: '100%',
              minHeight: '180px',
              padding: '14px 16px',
              borderRadius: '10px',
              background: 'rgba(0, 0, 0, 0.2)',
              border: '2px solid var(--border)',
              color: 'var(--text)',
              fontSize: '1rem',
              resize: 'vertical',
              lineHeight: '1.6',
              fontFamily: 'inherit',
              transition: 'all 0.3s ease',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--accent)';
              e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '8px'
          }}>
            <small style={{ 
              color: formData.reason.length > 1800 ? '#fbbf24' : 'var(--text-secondary)', 
              fontSize: '0.9rem',
              fontWeight: formData.reason.length > 1800 ? '600' : '400'
            }}>
              {formData.reason.length}/2000 characters
            </small>
            {formData.reason.length > 1800 && (
              <small style={{ color: '#fbbf24', fontSize: '0.85rem' }}>
                ⚠️ Approaching character limit
              </small>
            )}
          </div>
        </div>

        {/* Proof Screenshots */}
        <div style={{ marginBottom: '35px' }}>
          <label style={{ 
            display: 'flex', 
            marginBottom: '10px', 
            fontWeight: '700',
            fontSize: '1.05rem',
            color: 'var(--text)',
            alignItems: 'center',
            gap: '8px'
          }}>
            📸 Proof Screenshots <span style={{ 
              fontSize: '0.8rem', 
              fontWeight: '500',
              color: 'var(--text-secondary)',
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '3px 8px',
              borderRadius: '6px'
            }}>Max 10</span>
          </label>
          <div style={{
            position: 'relative',
            border: '2px dashed var(--border)',
            borderRadius: '12px',
            padding: '25px',
            background: 'rgba(0, 0, 0, 0.2)',
            transition: 'all 0.3s ease'
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.background = 'rgba(139, 92, 246, 0.05)';
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.2)';
          }}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleProofScreenshotsChange}
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: 'pointer'
              }}
            />
            {proofPreviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '10px' }}>🖼️</div>
                <p style={{ color: 'var(--text)', marginBottom: '5px', fontWeight: '600' }}>
                  Click to upload screenshots or drag and drop
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Upload up to 10 images as proof
                </p>
              </div>
            ) : (
              <div style={{ position: 'relative', pointerEvents: 'none' }}>
                <p style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: '0.9rem',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}>
                  Click anywhere to add more screenshots ({proofPreviews.length}/10)
                </p>
              </div>
            )}
          </div>
          {proofPreviews.length > 0 && (
            <div style={{ 
              marginTop: '20px', 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', 
              gap: '15px' 
            }}>
              {proofPreviews.map((preview, index) => (
                <div key={index} style={{ 
                  position: 'relative',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  border: '2px solid var(--border)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.03)';
                  e.currentTarget.style.borderColor = 'var(--accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}>
                  <img
                    src={preview}
                    alt={`Proof ${index + 1}`}
                    style={{ width: '100%', height: '160px', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    left: '8px',
                    background: 'rgba(0, 0, 0, 0.75)',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    #{index + 1}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeProofScreenshot(index)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'rgba(220, 38, 38, 0.95)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '1.3rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      fontWeight: 'bold',
                      lineHeight: '1'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.15)';
                      e.currentTarget.style.background = '#b91c1c';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.background = 'rgba(220, 38, 38, 0.95)';
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '15px', 
          justifyContent: 'flex-end',
          paddingTop: '20px',
          borderTop: '2px solid rgba(255, 255, 255, 0.1)'
        }}>
          <button
            type="button"
            onClick={() => navigate('/scammers')}
            disabled={submitting}
            style={{
              padding: '14px 30px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '2px solid var(--border)',
              borderRadius: '10px',
              color: 'var(--text)',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '1rem',
              transition: 'all 0.3s ease',
              opacity: submitting ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!submitting) {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.borderColor = 'var(--accent)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            style={{
              minWidth: '180px',
              padding: '14px 35px',
              background: submitting 
                ? 'rgba(139, 92, 246, 0.5)' 
                : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: '700',
              fontSize: '1.05rem',
              transition: 'all 0.3s ease',
              boxShadow: submitting ? 'none' : '0 4px 15px rgba(220, 38, 38, 0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!submitting) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!submitting) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(220, 38, 38, 0.3)';
              }
            }}
          >
            {submitting ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <span style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                Adding...
              </span>
            ) : (
              '➕ Add Scammer'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddScammer;
